import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuideErreursCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>10 Erreurs CV à Éviter Absolument en 2025 - Guide Complet</title>
                <meta name="description" content="Découvrez les 10 erreurs de CV les plus courantes qui font rejeter votre candidature : formatage, contenu, photo, longueur, mise à jour. Exemples avant/après et solutions." />
                <meta name="keywords" content="erreurs CV, fautes CV, erreurs à éviter CV, ce qu'il ne faut pas mettre dans un CV, erreurs CV recruteur, conseils CV éviter erreurs" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/erreurs-cv" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment éviter les erreurs de CV",
                  "description": "Identifiez et corrigez les erreurs de CV les plus courantes pour maximiser vos chances d'obtenir un entretien.",
                  "step": [
                    {"@type": "HowToStep", "name": "Vérifier le formatage et la mise en page", "text": "Contrôlez la cohérence des polices, marges, espaces et lisibilité générale."},
                    {"@type": "HowToStep", "name": "Corriger les fautes d'orthographe", "text": "Relisez attentivement et utilisez un correcteur orthographique."},
                    {"@type": "HowToStep", "name": "Supprimer les informations inutiles", "text": "Retirez les données personnelles non pertinentes (âge, situation familiale, photo si hors norme)."},
                    {"@type": "HowToStep", "name": "Vérifier la cohérence des dates", "text": "Assurez-vous qu'il n'y a pas de trous inexpliqués dans votre parcours."},
                    {"@type": "HowToStep", "name": "Adapter le CV à chaque offre", "text": "Personnalisez les mots-clés et l'accroche selon le poste visé."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">Erreurs CV</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">⚠️</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            10 Erreurs CV à Éviter Absolument en 2025
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Les erreurs qui font rejeter votre CV en quelques secondes et comment les éviter
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="erreurs-cv" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div className="space-y-8">
                    {[
                        { num: 1, title: "Erreurs d'orthographe et de grammaire", icon: "✍️", color: "red", content: "Les fautes d'orthographe et de grammaire sont la première cause de rejet d'un CV. Un CV avec des erreurs donne une image négligée et peu professionnelle." },
                        { num: 2, title: "Format incompatible avec les ATS", icon: "📄", color: "orange", content: "Les systèmes de recrutement automatisés (ATS) ne peuvent pas lire les CV en format image, PDF avec images scannées, ou formats exotiques. Utilisez un format Word ou PDF texte." },
                        { num: 3, title: "CV trop long ou trop court", icon: "📏", color: "yellow", content: "Un CV de plus de 2 pages pour un profil junior ou de moins d'une page pour un profil senior sont des signaux négatifs. Adaptez la longueur à votre expérience." },
                        { num: 4, title: "Informations obsolètes ou manquantes", icon: "🔄", color: "amber", content: "Un CV non mis à jour, avec des coordonnées obsolètes ou des informations manquantes (dates, entreprises) montre un manque de sérieux." },
                        { num: 5, title: "Photo inappropriée", icon: "📸", color: "pink", content: "Photo de vacances, selfie, photo de groupe, ou photo trop décontractée : la photo doit être professionnelle ou absente selon le secteur." },
                        { num: 6, title: "Manque de mots-clés pertinents", icon: "🔑", color: "purple", content: "L'absence de mots-clés liés au poste visé fait que votre CV ne ressort pas dans les recherches des recruteurs et des ATS." },
                        { num: 7, title: "Descriptions vagues et génériques", icon: "💬", color: "blue", content: "Des phrases comme 'Gestion de projets' sans détails, chiffres ou résultats concrets n'apportent aucune valeur et ne différencient pas votre profil." },
                        { num: 8, title: "Incohérences dans les dates", icon: "📅", color: "indigo", content: "Des chevauchements de dates, des écarts inexpliqués ou des incohérences chronologiques éveillent la suspicion des recruteurs." },
                        { num: 9, title: "Informations personnelles inutiles", icon: "🚫", color: "red", content: "État civil, nombre d'enfants, religion, opinions politiques : ces informations sont interdites et peuvent être discriminatoires." },
                        { num: 10, title: "CV non personnalisé pour le poste", icon: "🎯", color: "green", content: "Envoyer le même CV générique pour tous les postes montre un manque d'intérêt et réduit vos chances de correspondre aux attentes." }
                    ].map((erreur) => (
                        <div key={erreur.num} className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                            <div className="flex items-center mb-4">
                                <div className={`w-16 h-16 bg-gradient-to-r from-${erreur.color}-500 to-${erreur.color}-600 rounded-2xl flex items-center justify-center mr-4`}>
                                    <span className="text-3xl">{erreur.icon}</span>
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-gray-500 mb-1">ERREUR #{erreur.num}</div>
                                    <h2 className="text-2xl font-bold text-gray-900">{erreur.title}</h2>
                                </div>
                            </div>
                            <p className="text-gray-700 leading-relaxed mb-4">{erreur.content}</p>
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
                                <p className="text-sm font-semibold text-gray-700 mb-2">✅ Solution :</p>
                                <p className="text-gray-600">Relisez attentivement votre CV, utilisez un correcteur orthographique, adaptez le format, personnalisez le contenu pour chaque candidature.</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-3xl p-8 text-center text-white mt-16 mb-16">
                    <h2 className="text-3xl font-bold mb-4">Votre CV contient-il des erreurs qui vous coûtent des entretiens ?</h2>
                    <p className="text-xl mb-6">Notre IA détecte en 30 secondes les fautes, incohérences et points faibles — score et 3 conseils offerts</p>
                    <Link to="/" className="inline-block bg-white text-red-600 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Tester mon CV gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideErreursCV;








